/**
 * T49 — Local entitlement source-of-truth for offline / degraded
 * backend scenarios.
 *
 * The original flow:
 *   StoreKit → POST /iap/apple/apply → backend flips plan=pro →
 *   client `refresh()` → React state shows Pro.
 *
 * Failure mode: if the backend is temporarily down or slow when the
 * silent sync fires (sign-in, app resume), a paid Apple subscriber
 * sees "Free plan" in Settings until the next sync succeeds. App
 * Review fails the same way (sandbox subscriber, backend hiccup,
 * reviewer sees Free).
 *
 * Fix: cache the device's validated StoreKit entitlement in
 * localStorage and consider Pro UNLOCKED locally if Apple says
 *   - expirationDate is in the future
 *   - revocationDate is absent
 * regardless of what the backend currently returns. The backend
 * remains the long-running source of truth (renewals, refunds, fraud)
 * and reconciles on the next successful sync — when it returns Pro,
 * the cached entry is refreshed; when it returns Free AFTER the
 * cached expiration has elapsed, the cache is cleared.
 *
 * This is also exposed to the Connection Diagnostics screen so the
 * user / reviewer can SEE the entitlement state on-device even when
 * the backend is unreachable.
 */

const LS_KEY = "tiplingo_local_entitlement_v1";

/**
 * Persist a StoreKit transaction array as the device's locally-known
 * entitlement. Pass the array straight from `restore()` or
 * `getEntitlements()` — we extract only the bits we need (productId,
 * expirationDate, revocationDate, originalTransactionId,
 * transactionId).
 *
 * Idempotent — repeatedly persisting the same transaction is a no-op.
 * Picks the transaction with the furthest-future expirationDate so a
 * user with both monthly + yearly history is never downgraded by
 * replay.
 */
export function persistEntitlementFromTransactions(transactions) {
  if (!Array.isArray(transactions) || transactions.length === 0) return null;
  const valid = transactions
    .filter((tx) => tx && tx.expirationDate && !tx.revocationDate)
    .map((tx) => ({
      productId: tx.productId || null,
      transactionId: tx.transactionId || null,
      originalTransactionId: tx.originalTransactionId || null,
      purchaseDate: tx.purchaseDate || null,
      expirationDate: tx.expirationDate,
      revocationDate: tx.revocationDate || null,
    }));
  if (!valid.length) return null;
  valid.sort((a, b) => {
    const ad = Date.parse(a.expirationDate) || 0;
    const bd = Date.parse(b.expirationDate) || 0;
    return bd - ad;
  });
  const best = valid[0];
  const snap = {
    ...best,
    cachedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(snap));
  } catch {
    /* full / disabled — non-fatal */
  }
  return snap;
}

/**
 * Read the cached entitlement, IF present. Returns null when nothing
 * was ever persisted.
 */
export function getCachedEntitlement() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Pure check: is the cached entitlement currently valid by Apple's
 * own rules?
 *   - expirationDate must parse and be in the future
 *   - revocationDate must be absent
 * Used as the offline-tolerant "is the user Pro?" fallback when the
 * backend disagrees or is unreachable.
 */
export function isCachedEntitlementActive(now = Date.now()) {
  const snap = getCachedEntitlement();
  if (!snap || !snap.expirationDate) return false;
  if (snap.revocationDate) return false;
  const exp = Date.parse(snap.expirationDate);
  if (!Number.isFinite(exp)) return false;
  return exp > now;
}

/**
 * Clear the cached entitlement. Call this when:
 *   - The backend authoritatively returns plan=free AND the cache has
 *     already expired (i.e. the device's own clock confirms the lapse).
 *   - The user signs out.
 *   - Apple returns a revocationDate on a previously-cached tx.
 * We deliberately do NOT clear on a single failed backend sync —
 * transient outages must not flip a paid subscriber to Free.
 */
export function clearCachedEntitlement() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    /* non-fatal */
  }
}
